import React from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectSenderName } from 'flow/selectors/meeting-selectors';
import { selectVideoMemberByName } from 'flow/selectors/video-selectors';
import TileVideoParticipant from './TileVideoParticipant';
import TileDialInParticipant from './TileDialInParticipant';
import { FeedMember, FILL_STYLE } from '../meeting-tile-utils';

interface TileParticipantProps {
    memberObject: FeedMember;
    isCompact?: boolean;
    showPinControl?: boolean;
}

/**
 * A participant's slot in the grid: picks the primary tile variant —
 * TileVideoParticipant (has a video member) or TileDialInParticipant (phone,
 * no video member). While the member shares their screen, the primary camera
 * tile gives way to their camera on its secondary feed (the screen itself is
 * shown on the stage by TileRemoteStageScreenShare, not here).
 */
const TileParticipant: React.FC<TileParticipantProps> = ({
    memberObject,
    isCompact = false,
    showPinControl = false,
}) => {
    const member = memberObject.member;
    const displayName = member?.get('displayName');

    const ownName = useAppSelector(selectSenderName);
    const videoMember = useAppSelector((state) =>
        selectVideoMemberByName(state, displayName),
    );

    if (displayName === ownName) {
        return null;
    }

    const number = member.get('number');
    // Read off the roster member, which only this level holds: the tiles below
    // are handed a video member (no identity) or the key alone.
    const chatUserKey = member.getIn(['identity', 'chatUserKey']);
    const memberKey = member.get('key');
    const memberScreenShareActive = videoMember?.get('screenShareActive');
    const shouldRenderPrimary = !memberScreenShareActive;

    return (
        <>
            {shouldRenderPrimary && (
                // Always fills the positioned wrapper: a camera-off tile takes
                // its height from here to size the avatar, and an auto height
                // would collapse it (the tile declares container-type: size).
                <div style={FILL_STYLE}>
                    {videoMember ? (
                        <TileVideoParticipant
                            member={videoMember}
                            number={number}
                            chatUserKey={chatUserKey}
                            memberKey={memberKey}
                            isCompact={isCompact}
                            showPinControl={showPinControl}
                            pinFeedName={displayName}
                        />
                    ) : (
                        <TileDialInParticipant
                            member={member}
                            number={number}
                            chatUserKey={chatUserKey}
                            isCompact={isCompact}
                            showPinControl={showPinControl}
                            pinFeedName={displayName}
                        />
                    )}
                </div>
            )}

            {/* While the member shares their screen (shown on the stage), their
                camera rides along here as a secondary feed (secondaryPeer). */}
            {memberScreenShareActive && (
                <div key={`${memberKey}-secondary`} style={FILL_STYLE}>
                    <TileVideoParticipant
                        member={videoMember}
                        number={number}
                        chatUserKey={chatUserKey}
                        memberKey={memberKey}
                        isSecondary={true}
                        isCompact={isCompact}
                    />
                </div>
            )}
        </>
    );
};

// Memoized to short-circuit the resize hot path: this component runs several
// useAppSelector calls, and without memo it re-rendered (and re-evaluated all
// of them) for every tile on every resize tick, since MeetingMainArea rebuilds
// the tile elements each tick. Its props are stable across a resize.
export default React.memo(TileParticipant);
